// Centralized date/time formatting based on user preferences
// prefs: { date_input_format: 'US'|'EU', time_view_format: 'AMPM'|'24H' }

/**
 * Parse a date or datetime string into a Date anchored to the viewer's LOCAL
 * timezone.
 *
 * `new Date("2026-07-29")` parses a date-only string as UTC midnight; a viewer
 * west of UTC then sees it render as the previous day (Jul 28) via the local
 * getDate()/getMonth() formatters and calendar pickers - the classic
 * "date picker is one day earlier than I selected" bug. Building the Date from
 * explicit components keeps the calendar day stable regardless of timezone.
 *
 * - "YYYY-MM-DD"                -> local midnight of that day
 * - "YYYY-MM-DDTHH:MM[:SS]"     -> that local wall-clock time
 * - anything else               -> falls back to new Date(str)
 * Returns null for empty/invalid input.
 */
export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const [, y, mo, d, hh = '0', mm = '0', ss = '0'] = m;
    const dt = new Date(+y, +mo - 1, +d, +hh, +mm, +ss);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function formatDate(dt, prefs) {
  try {
    const d = dt instanceof Date ? dt : new Date(dt);
    if (Number.isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return prefs?.date_input_format === 'EU' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
  } catch {
    return '';
  }
}

export function formatTime(dt, prefs) {
  try {
    const d = dt instanceof Date ? dt : new Date(dt);
    if (Number.isNaN(d.getTime())) return '';
    if (prefs?.time_view_format === '24H') {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

export function formatWeekdayDate(dt, prefs) {
  try {
    const d = dt instanceof Date ? dt : new Date(dt);
    if (Number.isNaN(d.getTime())) return '';
    const wd = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
    const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    if (prefs?.date_input_format === 'EU') {
      // Sun, 22 Jun 2025
      return `${wd}, ${day} ${mon} ${year}`;
    }
    // US: Sun, Jun 22, 2025
    return `${wd}, ${mon} ${day}, ${year}`;
  } catch {
    return '';
  }
}

export function formatDateTime(dt, prefs) {
  const d = formatDate(dt, prefs);
  const t = formatTime(dt, prefs);
  if (!d && !t) return '';
  if (d && t) return `${d} ${t}`;
  return d || t;
}

export function parseDateInput(text, prefs) {
  // Convert user-entered date (US or EU) to ISO yyyy-mm-dd
  if (!text) return '';
  const parts = String(text).trim().split(/[\.\/-]/);
  if (parts.length < 3) return '';
  let dd, mm, yyyy;
  if (prefs?.date_input_format === 'EU') {
    [dd, mm, yyyy] = parts;
  } else {
    [mm, dd, yyyy] = parts;
  }
  if (yyyy?.length === 2) yyyy = `20${yyyy}`; // simple century
  return `${String(yyyy).padStart(4,'0')}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
}

export function parseTimeInput(text, prefs) {
  // Convert user-entered time to 24h HH:MM
  if (!text) return '';
  let s = String(text).trim();
  const ampmMatch = s.match(/\b(AM|PM)\b/i);
  let fmt = prefs?.time_view_format || (ampmMatch ? ampmMatch[1].toUpperCase() : '24H');
  s = s.replace(/\s*(AM|PM)\s*/ig, '');
  // Support hour-only
  if (/^\d{1,2}$/.test(s)) s = `${s.padStart(2,'0')}:00`;
  // Ensure HH:MM
  if (!/\d{1,2}:\d{2}/.test(s)) return '';
  let [hRaw, mRaw] = s.split(':');
  let h = parseInt(hRaw, 10);
  const m = mRaw.padStart(2, '0');
  if (fmt === 'PM' && h < 12) h += 12;
  if (fmt === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2,'0')}:${m}`;
}

/**
 * Format time from ISO string preserving the original timezone.
 * This avoids converting to browser's local timezone.
 *
 * Example: "2025-12-22T07:58:00+00:00" returns "7:58 AM" (not converted to browser TZ)
 *
 * @param {string} isoString - ISO 8601 datetime string with timezone offset
 * @param {object} prefs - User preferences { time_view_format: 'AMPM'|'24H' }
 * @returns {string} Formatted time string
 */
export function formatTimePreserveTimezone(isoString, prefs) {
  if (!isoString) return '';
  try {
    // Parse ISO string: 2025-12-22T07:58:00+00:00 or 2025-12-22T07:58:00Z
    // Extract time portion directly from the string to avoid timezone conversion
    const match = String(isoString).match(/T(\d{2}):(\d{2})/);
    if (!match) return '';

    let hour = parseInt(match[1], 10);
    const minute = match[2];

    if (prefs?.time_view_format === '24H') {
      return `${String(hour).padStart(2, '0')}:${minute}`;
    }

    // AM/PM format
    const period = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;

    return `${hour}:${minute} ${period}`;
  } catch {
    return '';
  }
}

/**
 * Format weekday date from ISO string preserving the original timezone.
 * This avoids converting to browser's local timezone.
 *
 * Example: "2025-12-22T07:58:00+00:00" returns "Sun, 22 Dec 2025" (EU) or "Sun, Dec 22, 2025" (US)
 *
 * @param {string} isoString - ISO 8601 datetime string with timezone offset
 * @param {object} prefs - User preferences { date_input_format: 'US'|'EU' }
 * @returns {string} Formatted weekday date string
 */
export function formatWeekdayDatePreserveTimezone(isoString, prefs) {
  if (!isoString) return '';
  try {
    // Extract date directly from ISO string: 2025-12-22T07:58:00+00:00
    const match = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      // Fall back to Date parsing if not ISO format
      const d = new Date(isoString);
      if (Number.isNaN(d.getTime())) return '';
      return formatWeekdayDate(d, prefs);
    }

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // 0-indexed for weekday calc
    const day = parseInt(match[3], 10);

    // Calculate weekday from date components (create date at noon to avoid DST issues)
    const d = new Date(year, month, day, 12, 0, 0);
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const wd = weekdays[d.getDay()];
    const mon = months[month];

    if (prefs?.date_input_format === 'EU') {
      // Sun, 22 Dec 2025
      return `${wd}, ${String(day).padStart(2, '0')} ${mon} ${year}`;
    }
    // US: Sun, Dec 22, 2025
    return `${wd}, ${mon} ${day}, ${year}`;
  } catch {
    return '';
  }
}

/**
 * Format date from ISO string preserving the original timezone.
 * This avoids converting to browser's local timezone.
 *
 * Example: "2018-09-03T18:00:00" returns "09/03/2018" (US) or "03/09/2018" (EU)
 *
 * @param {string} isoString - ISO 8601 datetime string
 * @param {object} prefs - User preferences { date_input_format: 'US'|'EU' }
 * @returns {string} Formatted date string
 */
export function formatDatePreserveTimezone(isoString, prefs) {
  if (!isoString) return '';
  try {
    // Extract date directly from ISO string: 2018-09-03T18:00:00
    const match = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      // Fall back to Date parsing if not ISO format
      const d = new Date(isoString);
      if (Number.isNaN(d.getTime())) return '';
      return formatDate(d, prefs);
    }

    const year = match[1];
    const month = match[2];
    const day = match[3];

    return prefs?.date_input_format === 'EU' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
  } catch {
    return '';
  }
}

/**
 * Format datetime from ISO string preserving the original timezone.
 * Combines formatDatePreserveTimezone and formatTimePreserveTimezone.
 *
 * Example: "2018-09-03T18:00:00" returns "09/03/2018 6:00 PM" (US) or "03/09/2018 18:00" (EU/24H)
 *
 * @param {string} isoString - ISO 8601 datetime string
 * @param {object} prefs - User preferences { date_input_format: 'US'|'EU', time_view_format: 'AMPM'|'24H' }
 * @returns {string} Formatted datetime string
 */
export function formatDateTimePreserveTimezone(isoString, prefs) {
  if (!isoString) return '';
  const d = formatDatePreserveTimezone(isoString, prefs);
  const t = formatTimePreserveTimezone(isoString, prefs);
  if (!d && !t) return '';
  if (d && t) return `${d} ${t}`;
  return d || t;
}

/**
 * Format a timestamp in browser's local timezone with explicit timezone indicator.
 * Use this for "last updated" timestamps where the user's local time is relevant.
 *
 * Example: "22/12/2025, 10:20:14 (GMT+2)" or "12/22/2025, 10:20:14 AM (GMT+2)"
 *
 * @param {string|Date} dt - Date/time to format
 * @param {object} prefs - User preferences { date_input_format: 'US'|'EU', time_view_format: 'AMPM'|'24H' }
 * @returns {string} Formatted datetime string with timezone
 */
export function formatDateTimeWithTimezone(dt, prefs) {
  if (!dt) return '';
  try {
    const d = dt instanceof Date ? dt : new Date(dt);
    if (Number.isNaN(d.getTime())) return '';

    // Format date
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const dateStr = prefs?.date_input_format === 'EU'
      ? `${day}/${month}/${year}`
      : `${month}/${day}/${year}`;

    // Format time with seconds
    const hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    let timeStr;
    if (prefs?.time_view_format === '24H') {
      timeStr = `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
    } else {
      const period = hours >= 12 ? 'PM' : 'AM';
      let hour12 = hours;
      if (hour12 === 0) hour12 = 12;
      else if (hour12 > 12) hour12 -= 12;
      timeStr = `${hour12}:${minutes}:${seconds} ${period}`;
    }

    // Get timezone offset string (e.g., "GMT+2" or "GMT-5")
    const offsetMinutes = -d.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const tzStr = offsetMins > 0
      ? `GMT${offsetSign}${offsetHours}:${String(offsetMins).padStart(2, '0')}`
      : `GMT${offsetSign}${offsetHours}`;

    return `${dateStr}, ${timeStr} (${tzStr})`;
  } catch {
    return '';
  }
}
